<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { BaseTable } from 'dashboard/components-next/table';
import templateButtonsImage from 'dashboard/assets/images/conversation-flows/templates/buttons.webp';
import templateCarouselImage from 'dashboard/assets/images/conversation-flows/templates/carosel.webp';
import templateListImage from 'dashboard/assets/images/conversation-flows/templates/list.webp';
import templateWelcomeImage from 'dashboard/assets/images/conversation-flows/templates/welcom.webp';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import MobileConversationFlowRow from './components/MobileConversationFlowRow.vue';
import { mobileFlowTemplateIds } from './mobileFlowTemplates';

const router = useRouter();
const store = useStore();
const { t, locale } = useI18n();

const flows = useMapGetter('conversationFlows/getConversationFlows');
const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('conversationFlows/getUIFlags');

const searchQuery = ref('');
const selectedFlow = ref({});
const selectedTemplate = ref({});
const loading = ref({});
const deleteDialogRef = ref(null);
const previewDialogRef = ref(null);

const availableWhatsAppInbox = computed(() =>
  inboxes.value.find(inbox => {
    return (
      inbox.channel_type === INBOX_TYPES.WHATSAPP ||
      (inbox.channel_type === INBOX_TYPES.TWILIO &&
        inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP)
    );
  })
);

const sortedFlows = computed(() =>
  [...flows.value].sort((left, right) => {
    const leftTime = new Date(
      left.updated_on || left.updated_at || 0
    ).getTime();
    const rightTime = new Date(
      right.updated_on || right.updated_at || 0
    ).getTime();
    return rightTime - leftTime;
  })
);

const filteredFlows = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return sortedFlows.value;
  return picoSearch(sortedFlows.value, query, [
    'name',
    'description',
    'status',
  ]);
});

const previewCatalog = {
  welcome_buttons: templateWelcomeImage,
  chatbot_buttons: templateButtonsImage,
  chatbot_list: templateListImage,
  cards_carousel: templateCarouselImage,
};

const templates = computed(() => {
  const currentLocale = locale.value;
  if (!currentLocale) return [];

  const catalog = {
    welcome_buttons: {
      id: 'welcome_buttons',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.DESCRIPTION'
      ),
    },
    chatbot_buttons: {
      id: 'chatbot_buttons',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.DESCRIPTION'
      ),
    },
    chatbot_list: {
      id: 'chatbot_list',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.DESCRIPTION'
      ),
    },
    cards_carousel: {
      id: 'cards_carousel',
      title: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.TITLE'),
      description: t(
        'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.DESCRIPTION'
      ),
    },
  };

  return mobileFlowTemplateIds.map(templateId => ({
    ...catalog[templateId],
    heroImage: previewCatalog[templateId],
  }));
});

const openFlow = flowId => {
  router.push({
    name: 'mobile_conversation_flow_editor',
    params: { flowId },
  });
};

const createFlow = () => {
  if (!availableWhatsAppInbox.value) {
    useAlert(t('CONVERSATION_FLOWS.CREATE.NO_INBOX_MESSAGE'));
    return;
  }

  router.push({
    name: 'mobile_conversation_flow_editor',
    params: { flowId: 'new' },
    query: { inboxId: availableWhatsAppInbox.value.id },
  });
};

const createTemplateFlow = templateId => {
  if (!availableWhatsAppInbox.value) {
    useAlert(t('CONVERSATION_FLOWS.CREATE.NO_INBOX_MESSAGE'));
    return;
  }

  router.push({
    name: 'mobile_conversation_flow_editor',
    params: { flowId: 'new' },
    query: {
      inboxId: availableWhatsAppInbox.value.id,
      templateId,
    },
  });
};

const openTemplatePreview = template => {
  selectedTemplate.value = template;
  previewDialogRef.value.open();
};

const openDeletePopup = flow => {
  selectedFlow.value = flow;
  deleteDialogRef.value.open();
};

const confirmDeletion = async () => {
  loading.value[selectedFlow.value.id] = true;
  try {
    await store.dispatch('conversationFlows/delete', selectedFlow.value.id);
    useAlert(t('CONVERSATION_FLOWS.DELETE.SUCCESS_MESSAGE'));
    deleteDialogRef.value.close();
  } catch (error) {
    useAlert(t('CONVERSATION_FLOWS.DELETE.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedFlow.value.id] = false;
    selectedFlow.value = {};
  }
};

const exportFlow = flow => {
  const payload = {
    name: flow.name,
    channel_type: flow.channel_type,
    inbox_id: flow.inbox_id,
    definition: flow.definition,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${flow.name || 'mobile-flow-builder'}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
};

onMounted(() => {
  store.dispatch('conversationFlows/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('CONVERSATION_FLOWS.INDEX.EMPTY_STATE.DESCRIPTION')"
    :loading-message="$t('CONVERSATION_FLOWS.LOADING')"
    :show-pagination-footer="false"
  >
    <div class="flex w-full flex-col gap-6">
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('MOBILE_CONVERSATION_FLOWS.INDEX.TITLE')"
        :description="$t('MOBILE_CONVERSATION_FLOWS.INDEX.DESCRIPTION')"
        :search-placeholder="$t('CONVERSATION_FLOWS.SEARCH_PLACEHOLDER')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus-circle"
            :label="$t('CONVERSATION_FLOWS.CREATE.BUTTON')"
            @click="createFlow"
          />
        </template>
      </BaseSettingsHeader>

      <section class="rounded-3xl border border-n-slate-3 bg-white p-5">
        <div class="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ $t('MOBILE_CONVERSATION_FLOWS.TEMPLATES.TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('MOBILE_CONVERSATION_FLOWS.TEMPLATES.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div class="grid gap-4 xl:grid-cols-4 md:grid-cols-2 grid-cols-1">
          <article
            v-for="template in templates"
            :key="template.id"
            class="flex flex-col rounded-3xl border border-n-slate-3 bg-n-slate-1 p-4"
          >
            <div
              class="mb-3 overflow-hidden rounded-2xl border border-n-slate-3 bg-white"
            >
              <img
                :src="template.heroImage"
                :alt="template.title"
                class="h-56 w-full object-contain"
              />
            </div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ template.title }}
            </h3>
            <p class="mt-2 line-clamp-2 text-sm text-n-slate-11">
              {{ template.description }}
            </p>
            <div class="mt-4 grid grid-cols-2 gap-2">
              <Button
                secondary
                :label="$t('CONVERSATION_FLOWS.TEMPLATES.PREVIEW')"
                @click="openTemplatePreview(template)"
              />
              <Button
                :label="$t('CONVERSATION_FLOWS.TEMPLATES.USE_TEMPLATE')"
                @click="createTemplateFlow(template.id)"
              />
            </div>
          </article>
        </div>
      </section>

      <section class="rounded-3xl border border-n-slate-3 bg-white">
        <BaseTable
          :headers="[
            '#',
            $t('CONVERSATION_FLOWS.TABLE_HEADER.FLOW_NAME'),
            $t('CONVERSATION_FLOWS.TABLE_HEADER.STATUS'),
            $t('CONVERSATION_FLOWS.TABLE_HEADER.INBOX'),
            $t('CONVERSATION_FLOWS.TABLE_HEADER.UPDATED_AT'),
            $t('CONVERSATION_FLOWS.TABLE_HEADER.ACTIONS'),
          ]"
          :items="filteredFlows"
          :is-loading="uiFlags.isFetching"
          :no-data-message="
            searchQuery
              ? $t('CONVERSATION_FLOWS.NO_RESULTS')
              : $t('CONVERSATION_FLOWS.INDEX.EMPTY_STATE.DESCRIPTION')
          "
          class="rounded-3xl"
        >
          <template #row="{ items }">
            <MobileConversationFlowRow
              v-for="(flow, index) in items"
              :key="flow.id"
              :flow="flow"
              :index="index + 1"
              :loading="Boolean(loading[flow.id])"
              @open="openFlow"
              @export="exportFlow"
              @delete="openDeletePopup"
            />
          </template>
        </BaseTable>
      </section>
    </div>

    <Dialog
      ref="previewDialogRef"
      :title="selectedTemplate.title"
      :confirm-button-label="$t('CONVERSATION_FLOWS.TEMPLATES.USE_TEMPLATE')"
      :cancel-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_NO')"
      @confirm="createTemplateFlow(selectedTemplate.id)"
    >
      <div class="flex flex-col gap-4">
        <div
          class="overflow-hidden rounded-3xl border border-n-slate-3 bg-white"
        >
          <img
            :src="selectedTemplate.heroImage"
            :alt="selectedTemplate.title"
            class="h-[30rem] w-full object-contain"
          />
        </div>
        <p class="text-sm text-n-slate-11">
          {{ selectedTemplate.description }}
        </p>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      :title="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_TITLE')"
      :description="
        $t('CONVERSATION_FLOWS.DELETE.CONFIRM_MESSAGE', {
          flowName: selectedFlow.name,
        })
      "
      :confirm-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_YES')"
      :cancel-button-label="$t('CONVERSATION_FLOWS.DELETE.CONFIRM_NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
